import FetchModule from '../../Model/Share/Network/FetchModule';

export const POLL_TIMEOUT_MS = 435000;

// 기존 Spring 인증·갱신 규칙으로 api3을 호출한다. 취소 신호는 갱신 뒤 재시도에도 유지한다. //
export async function requestTurn(path, method, body, signal) {
  const module = new FetchModule();
  const config = () => ({ ...(body === undefined ? module.getConfig(method) : module.postConfig(method, body)), signal });
  const url = `${module.baseURL}api3/ai-agent/run/${path}`;
  let response = await fetch(url, config());
  let result = await response.json();

  if (result.status === 401 && !signal.aborted) {
    await module.refreshAuthorization();
    if (signal.aborted) throw new Error('cancelled');

    response = await fetch(url, config());
    result = await response.json();
  }

  if (result.status === 403) module.handleForbidden();

  return result;
}

// 접수 이후 서버 턴은 계속된다. 취소·이전 질문의 응답은 화면에 전달하지 않는다. //
export async function runAcceptedTurn({ input, sessionId, turn, onProgress, request = requestTurn, now = Date.now }) {
  if (turn.cancelled) return null;

  const started = now();
  turn.deadlineTimer = setTimeout(() => {
    turn.timedOut = true;
    turn.controller.abort();
    clearTimeout(turn.pollTimer);
    turn.wake?.();
  }, POLL_TIMEOUT_MS);

  try {
    const accepted = await request('accept', 'POST', { input, sessionId }, turn.controller.signal);

    if (turn.cancelled) return null;
    if (accepted?.status === 409) {
      const error = new Error('turn already running');

      error.code = 'TURN_RUNNING';
      throw error;
    }
    if (accepted?.status !== 200 || !accepted?.data?.turnId) throw new Error(accepted?.message || 'accept failed');

    const turnId = accepted.data.turnId;

    while (!turn.cancelled && !turn.timedOut && now() - started < POLL_TIMEOUT_MS) {
      const response = await request(encodeURIComponent(turnId), 'GET', undefined, turn.controller.signal);

      if (turn.cancelled) return null;
      if (response?.status !== 200 || !response.data) throw new Error(response?.message || 'poll failed');

      const data = response.data;

      if (data.state === 'done' || data.state === 'failed') return data;
      if (data.state !== 'running') throw new Error('unknown turn state');

      onProgress(data.progressJson);
      await new Promise(resolve => {
        turn.wake = resolve;
        turn.pollTimer = setTimeout(resolve, now() - started < 15000 ? 1000 : 3000);
      });
      turn.wake = null;
      turn.pollTimer = null;
    }

    if (turn.cancelled) return null;
    throw new Error('poll timeout');
  }
  catch (error) {
    if (turn.timedOut) throw new Error('poll timeout');
    throw error;
  }
  finally { clearTimeout(turn.deadlineTimer); }
}

// 질문마다 만든 타이머와 요청을 해제한다. 새 질문 상태를 건드리지 않는다. //
export function stopTurn(turn) {
  if (!turn) return;

  turn.cancelled = true;
  turn.controller.abort();
  clearInterval(turn.loadingTimer);
  clearTimeout(turn.pollTimer);
  clearTimeout(turn.deadlineTimer);
  turn.wake?.();
}

// 긴 산문 문단을 두 문장씩 나눈다. 기존 줄바꿈·목록·코드 블록은 보존한다. //
export function formatTurnParagraphs(text) {
  if (typeof text !== 'string' || typeof Intl.Segmenter !== 'function') return text;
  if (/^\s*(?:```|~~~)/m.test(text)) return text;

  const segmenter = new Intl.Segmenter(undefined, { granularity: 'sentence' });

  return text.split(/(\r?\n[ \t]*\r?\n)/).map(block => {
    if (/\n|[*_`]|\[[^\]]*\]\(|^\s*(?:#{1,6}\s|[-+>]\s|\d+[.)]\s)/.test(block)) return block;

    const sentences = Array.from(segmenter.segment(block), part => part.segment);

    if (sentences.length <= 2) return block;

    const paragraphs = [];

    for (let index = 0; index < sentences.length; index += 2) {
      paragraphs.push(sentences.slice(index, index + 2).join('').trim());
    }

    return paragraphs.join('\n\n');
  }).join('');
}

// 원문과 동일한 표 설명만 다시 나눠, 저장 과정에서 사라진 문단 경계를 복원한다. //
function restoreTableParagraphs(parsed) {
  const display = parsed.displayItems;

  if (!display || typeof display !== 'object' || Array.isArray(display)) return display;

  const existing = [display.intro, display.outro].filter(Boolean).join('\n');
  const compact = text => text.replace(/\s+/g, '');
  let intro = display.intro;
  let outro = display.outro;

  if (existing && compact(existing) === compact(parsed.text)) {
    const boundary = /\r?\n[ \t]*\r?\n/.exec(parsed.text);

    if (boundary) {
      intro = parsed.text.slice(0, boundary.index);
      outro = parsed.text.slice(boundary.index + boundary[0].length);
    }
  }

  return { ...display, ...(intro ? { intro: formatTurnParagraphs(intro) } : {}),
    ...(outro ? { outro: formatTurnParagraphs(outro) } : {}) };
}

// 새 답변과 저장된 대화에 동일한 문단·화면 채널 복원을 적용한다. //
export function decodeTurnRender(renderJson) {
  try {
    const parsed = JSON.parse(renderJson);

    if (!parsed || typeof parsed.text !== 'string') return {};

    return {
      text: formatTurnParagraphs(parsed.text),
      ...(parsed.displayItems ? { displayItems: restoreTableParagraphs(parsed) } : {}),
      ...(Array.isArray(parsed.extraDisplays) && parsed.extraDisplays.length ? { extraDisplays: parsed.extraDisplays } : {}),
      ...(parsed.vizData ? { vizData: parsed.vizData } : {}),
      ...(parsed.salesViewData?.weeksData ? { salesViewData: parsed.salesViewData } : {}),
      ...(parsed.revenueChartData ? { revenueChartData: parsed.revenueChartData } : {}),
      ...(parsed.smsRecommendation ? { smsRecommendation: parsed.smsRecommendation } : {}),
    };
  }
  catch {
    return {};
  }
}
